import { z } from 'zod';
import { IsoDateTime, Uuid, patchOf } from './common';
import { StoredFile } from './files';
import { PurchasePayment } from './inventory';

const Centavos = z.number().int().min(0).max(1_000_000_000);
const Positive = Centavos.refine((v) => v > 0, 'Informe um valor maior que zero.');
export const Month = z
  .string()
  .regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'Use YYYY-MM')
  .describe('Calendar month `YYYY-MM` (São Paulo).');

export const ExpenseCategory = z.enum([
  'filament',
  'supplies',
  'equipment',
  'maintenance',
  'software',
  'ads',
  'taxes',
  'shipping',
  'packaging',
  'other',
]);
export type ExpenseCategory = z.infer<typeof ExpenseCategory>;
export const EXPENSE_CATEGORY_LABELS: Record<ExpenseCategory, string> = {
  filament: 'Filamento',
  supplies: 'Insumos',
  equipment: 'Equipamento',
  maintenance: 'Manutenção',
  software: 'Software',
  ads: 'Anúncios',
  taxes: 'Impostos',
  shipping: 'Frete',
  packaging: 'Embalagem',
  other: 'Outros',
};
/**
 * Categories counted as the month's fixed costs (break-even). The others are already inside each
 * piece's cost (material, supplies, packaging, maintenance per hour, shipping charges) or are
 * equipment (depreciation). Recurring items always count.
 */
export const FIXED_COST_CATEGORIES: readonly ExpenseCategory[] = [
  'taxes',
  'software',
  'ads',
  'other',
];
export const isFixedCost = (e: { category: ExpenseCategory; recurringId?: string | null }) =>
  FIXED_COST_CATEGORIES.includes(e.category) || !!e.recurringId;

export const ExpenseStatus = z.enum(['paid', 'pending']);
export type ExpenseStatus = z.infer<typeof ExpenseStatus>;

/** Installments: count and first due date; amounts split equally (remainder on the first). */
export const InstallmentPlanBody = z
  .object({
    count: z.number().int().min(1).max(60),
    firstDueOn: z.iso.date(),
    totalCentavos: Positive.optional().describe('Defaults to the equipment value.'),
  })
  .meta({ id: 'InstallmentPlanBody' });
export type InstallmentPlanBody = z.input<typeof InstallmentPlanBody>;

const ExpenseFields = z.object({
  spentOn: z.iso.date(),
  description: z.string().trim().min(1, 'Descreva a despesa.').max(200),
  category: ExpenseCategory,
  amountCentavos: Positive,
  paymentMethod: PurchasePayment,
  supplier: z.string().trim().max(120).nullable().default(null),
  notes: z.string().trim().max(2000).nullable().default(null),
});

export const CreateExpenseBody = ExpenseFields.extend({
  fileIds: z
    .array(Uuid)
    .max(10)
    .default([])
    .describe('Receipts uploaded with `POST /files` (kind `document` or `image`).'),
  equipment: z
    .object({
      lifeMonths: z.number().int().min(1).max(240),
      installments: InstallmentPlanBody.omit({ totalCentavos: true }).nullable().default(null),
    })
    .nullable()
    .default(null)
    .describe(
      '"É equipamento?": the expense becomes an asset (outside the month total) and an equipment record that depreciates monthly over its life.',
    ),
}).meta({ id: 'CreateExpenseBody' });
export type CreateExpenseBody = z.input<typeof CreateExpenseBody>;

export const UpdateExpenseBody = patchOf(ExpenseFields).meta({ id: 'UpdateExpenseBody' });
export type UpdateExpenseBody = z.input<typeof UpdateExpenseBody>;

export const LaunchExpenseBody = z
  .object({
    amountCentavos: Positive,
    paymentMethod: PurchasePayment,
    spentOn: z.iso.date().optional().describe('Defaults to the day it was due.'),
  })
  .meta({ id: 'LaunchExpenseBody' });
export type LaunchExpenseBody = z.input<typeof LaunchExpenseBody>;

export const ExpenseFileBody = z.object({ fileId: Uuid });

export const Expense = z
  .object({
    id: Uuid,
    spentOn: z.iso.date(),
    description: z.string(),
    category: ExpenseCategory,
    amountCentavos: z.number().int().nullable().describe('Null while a variable item is pending.'),
    paymentMethod: PurchasePayment.nullable(),
    supplier: z.string().nullable(),
    notes: z.string().nullable(),
    status: ExpenseStatus.describe('`pending` = "a lançar": confirm the value with /launch.'),
    fixedCost: z.boolean().describe('Counts in the month’s fixed costs (break-even).'),
    asset: z.boolean().describe('Equipment: outside the month total; depreciates instead.'),
    recurring: z.object({ id: Uuid, name: z.string() }).nullable(),
    purchase: z.object({ id: Uuid, kind: z.enum(['filament', 'supplies']) }).nullable(),
    maintenance: z.object({ id: Uuid, printerId: Uuid, printerName: z.string() }).nullable(),
    equipment: z.object({ id: Uuid, name: z.string() }).nullable(),
    locked: z
      .string()
      .nullable()
      .describe('Why amount, date and category cannot change here (purchase or maintenance).'),
    files: z.array(StoredFile),
    createdAt: IsoDateTime,
  })
  .meta({ id: 'Expense' });
export type Expense = z.infer<typeof Expense>;

export const ExpenseQuery = z.object({
  month: Month.optional().describe('Defaults to the current month.'),
  category: ExpenseCategory.optional(),
  status: ExpenseStatus.optional(),
});

export const UpcomingOut = z.object({
  kind: z.enum(['installment', 'recurring']),
  label: z.string(),
  dueOn: z.iso.date(),
  amountCentavos: z.number().int().nullable(),
  refId: Uuid.describe('Installment or pending expense id.'),
});
export type UpcomingOut = z.infer<typeof UpcomingOut>;

export const ExpenseList = z
  .object({
    month: Month,
    data: z.array(Expense),
    totals: z.object({
      totalCentavos: z.number().int().describe('Paid expenses, without assets.'),
      byCategory: z.array(
        z.object({ category: ExpenseCategory, totalCentavos: z.number().int(), count: z.number() }),
      ),
      pendingCount: z.number().int(),
      assetCount: z.number().int(),
      assetCentavos: z.number().int(),
    }),
    upcoming: z.array(UpcomingOut).describe('"Próximas saídas": installments and pending items.'),
  })
  .meta({ id: 'ExpenseList' });
export type ExpenseList = z.infer<typeof ExpenseList>;

// ---- Recurring --------------------------------------------------------------------------------

const RecurringFields = z.object({
  name: z.string().trim().min(1, 'Dê um nome.').max(120),
  category: ExpenseCategory,
  day: z.number().int().min(1).max(28).describe('Day of the month (1–28).'),
  amountCentavos: Positive.nullable().default(null).describe('Null = variable ("a lançar").'),
  active: z.boolean().default(true),
});
export const CreateRecurringBody = RecurringFields.meta({ id: 'CreateRecurringBody' });
export type CreateRecurringBody = z.input<typeof CreateRecurringBody>;
export const UpdateRecurringBody = patchOf(RecurringFields).meta({ id: 'UpdateRecurringBody' });
export type UpdateRecurringBody = z.input<typeof UpdateRecurringBody>;

export const RecurringExpense = z
  .object({
    id: Uuid,
    name: z.string(),
    category: ExpenseCategory,
    day: z.number().int(),
    amountCentavos: z.number().int().nullable(),
    active: z.boolean(),
    startsOn: z.iso.date(),
    nextOn: z.iso.date().nullable().describe('Next day it becomes "a lançar"; null when paused.'),
  })
  .meta({ id: 'RecurringExpense' });
export type RecurringExpense = z.infer<typeof RecurringExpense>;

// ---- Equipment and installments ------------------------------------------------------------

export const Installment = z.object({
  id: Uuid,
  number: z.number().int(),
  count: z.number().int(),
  dueOn: z.iso.date(),
  amountCentavos: z.number().int(),
  paidAt: IsoDateTime.nullable(),
  paidBy: z.string().nullable(),
});
export type Installment = z.infer<typeof Installment>;

export const Equipment = z
  .object({
    id: Uuid,
    name: z.string(),
    kind: z.enum(['printer', 'other']),
    printer: z
      .object({
        id: Uuid,
        lifetimeHours: z.number().int(),
        hoursPrinted: z.number(),
        depreciationPerHourCentavos: z.number(),
        retired: z.boolean(),
      })
      .nullable()
      .describe('Printers depreciate per printed hour (inside each piece’s cost).'),
    valueCentavos: z.number().int(),
    purchasedOn: z.iso.date().nullable(),
    lifeMonths: z.number().int().nullable(),
    monthlyCostCentavos: z
      .number()
      .int()
      .nullable()
      .describe('Other equipment: value ÷ life in months, a fixed cost while in its life.'),
    depreciatedCentavos: z.number().int(),
    remainingCentavos: z.number().int(),
    depreciatedBps: z.number().int(),
    supplier: z.string().nullable(),
    notes: z.string().nullable(),
    installments: z.array(Installment),
    paidCentavos: z.number().int(),
    unpaidCentavos: z.number().int(),
    nextDue: Installment.nullable(),
  })
  .meta({ id: 'Equipment' });
export type Equipment = z.infer<typeof Equipment>;

const EquipmentFields = z.object({
  name: z.string().trim().min(1, 'Dê um nome.').max(120),
  valueCentavos: Positive,
  purchasedOn: z.iso.date(),
  lifeMonths: z.number().int().min(1).max(240),
  supplier: z.string().trim().max(120).nullable().default(null),
  notes: z.string().trim().max(2000).nullable().default(null),
});
export const CreateEquipmentBody = EquipmentFields.extend({
  paymentMethod: PurchasePayment.default('credit_card'),
  installments: InstallmentPlanBody.omit({ totalCentavos: true }).nullable().default(null),
}).meta({ id: 'CreateEquipmentBody' });
export type CreateEquipmentBody = z.input<typeof CreateEquipmentBody>;
export const UpdateEquipmentBody = patchOf(EquipmentFields).meta({ id: 'UpdateEquipmentBody' });
export type UpdateEquipmentBody = z.input<typeof UpdateEquipmentBody>;

export const UpdateInstallmentBody = z
  .object({ dueOn: z.iso.date().optional(), amountCentavos: Positive.optional() })
  .meta({ id: 'UpdateInstallmentBody' });

/** Splits a total into `count` equal installments; the remainder goes on the first. */
export function splitInstallments(totalCentavos: number, count: number): number[] {
  const base = Math.floor(totalCentavos / count);
  const rest = totalCentavos - base * count;
  return Array.from({ length: count }, (_, i) => (i === 0 ? base + rest : base));
}
